import { Component, OnDestroy } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { LoginService } from '@src/app/services/login/login.service';
import { Router } from '@angular/router';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { PermissionsService } from '@src/app/services/permissions/permissions.service';
import { getPermissions, PermissionName, User } from '@src/app/model/userdata';
import { RetailerScope } from '@src/app/model/retailer-scope';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentConfig } from '@src/app/configs/environment.config';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  public isHandset$: Observable<boolean>;

  public permAccessUsersRead$ = new BehaviorSubject(false);
  public permAccessRetailersRead$ = new BehaviorSubject(false);
  public permAccessRetailerDataRead$ = new BehaviorSubject(false);
  public permAccessRetailerSettingsRead$ = new BehaviorSubject(false);
  public permAccessConfigurationsRead$ = new BehaviorSubject(false);
  public permAccessModulesRead$ = new BehaviorSubject(false);
  public permAccessMaterialsRead$ = new BehaviorSubject(false);
  public permAccessCatalogsRead$ = new BehaviorSubject(false);
  public permAccessEansRead$ = new BehaviorSubject(false);

  public readonly portalVersion: string;

  constructor(
    protected readonly _ENV_CONFIG: EnvironmentConfig,
    private _router: Router,
    private _breakpointObserver: BreakpointObserver,
    private _loginService: LoginService,
    private _permissionsService: PermissionsService,
    private _translateService: TranslateService,
    public scopeService: ScopeService
  ) {
    this.portalVersion = this._ENV_CONFIG.version
      ? this._ENV_CONFIG.version
      : this._ENV_CONFIG.build.commit + ' - ' + this._ENV_CONFIG.build.number;
    this.isHandset$ = this._breakpointObserver.observe(Breakpoints.Handset)
      .pipe(
        map(result => result.matches),
        shareReplay()
      );
    this._loginService.setCurrentUser();

    this._loginService.getCurrentUser$()
      .pipe(
        takeUntil(this._componentDestroyed$),
        filter(u => !!u)
      )
      .subscribe(u => this.updatePermissions(u));
  }

  logout(): void {
    this._loginService.logout$().subscribe({
      next: () => {
        this.scopeService.resetScope();
        this._router.navigateByUrl('/');
      },
      // also redirect when logout in server failed
      error: () => this._router.navigateByUrl('/'),
    });
  }

  openDraftPortal(): void {
    const jwt = this._loginService.getJwt();
    const scope = this.scopeService.currentScope$.getValue()?.id;
    if (scope && jwt) {
      const argString = `?scope=${scope}&usePortal=true`;
      window.open(`${this._ENV_CONFIG.configuratorUrl}/${argString}#${jwt}`, '_blank');
    }
  }

  setScope(scope: RetailerScope): void {
    this.scopeService.setScope(scope);
    window.location.reload();
  }

  /**
   * Update specific permissions for the given user.
   *
   * @param user
   * @private
   */
  private updatePermissions(user: User) {
    this.permAccessUsersRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_users', PermissionName.Read));
    this.permAccessRetailersRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_retailers', PermissionName.Read));
    this.permAccessRetailerDataRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_retailer_data', PermissionName.Read));
    this.permAccessRetailerSettingsRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_retailer_settings', PermissionName.Read));
    this.permAccessConfigurationsRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_configurations', PermissionName.Read));
    this.permAccessModulesRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_modules', PermissionName.Read));
    this.permAccessCatalogsRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_catalogs', PermissionName.Read));
    this.permAccessMaterialsRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_materials', PermissionName.Read));
    this.permAccessEansRead$.next(this._permissionsService.isPermitted(
      getPermissions(user.roles), 'access_eans', PermissionName.Read));
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }
}
